import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getMyNews, deleteNews } from '../lib/api';
import { Plus, Edit, Trash2, Clock, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fmt = d => new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
const SM = {
  pending: { l: 'PENDIENTE', c: 'border-l-amber-500 bg-amber-50' },
  approved: { l: 'APROBADA', c: 'border-l-emerald-600 bg-emerald-50' },
  rejected: { l: 'RECHAZADA', c: 'border-l-[#E50B15] bg-red-50' },
};

export default function DashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [myNews, setMyNews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch_ = useCallback(async () => {
    try { const { data } = await getMyNews(); setMyNews(data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!user) { nav('/login'); return; } fetch_(); }, [user, nav, fetch_]);

  const del = async (id) => {
    if (!window.confirm('¿Eliminar?')) return;
    try { await deleteNews(id); toast.success('Eliminada'); setMyNews(p => p.filter(n => n.id !== id)); }
    catch { toast.error('Error'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-7 w-7 animate-spin text-[#E50B15]" /></div>;

  return (
    <div data-testid="dashboard-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-8 pb-4 border-b-4 border-[#0B1F40]">
        <div>
          <span className="text-[10px] font-black tracking-[0.3em] uppercase text-[#E50B15]">DASHBOARD</span>
          <h1 className="text-2xl sm:text-3xl tracking-tighter text-[#0B1F40]" style={{ fontFamily: 'Archivo Black' }}>MIS NOTICIAS</h1>
        </div>
        <Link to="/create-news">
          <button data-testid="create-news-btn" className="bg-[#E50B15] text-white text-[10px] font-black h-9 px-5 flex items-center gap-1.5 brutal-btn-press transition-all" style={{ boxShadow: '3px 3px 0px #0B1F40' }}>
            <Plus className="h-3.5 w-3.5" /> NUEVA NOTICIA
          </button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="border-2 border-[#0B1F40] p-4 text-center brutal-shadow">
          <p className="text-3xl font-black text-[#0B1F40]" style={{ fontFamily: 'Archivo Black' }}>{myNews.length}</p>
          <p className="text-[9px] font-black tracking-[0.2em] uppercase text-[#0B1F40]/40">TOTAL</p>
        </div>
        <div className="border-2 border-emerald-600 p-4 text-center bg-emerald-50">
          <p className="text-3xl font-black text-emerald-700" style={{ fontFamily: 'Archivo Black' }}>{myNews.filter(n => n.status === 'approved').length}</p>
          <p className="text-[9px] font-black tracking-[0.2em] uppercase text-emerald-600/60">APROBADAS</p>
        </div>
        <div className="border-2 border-amber-500 p-4 text-center bg-amber-50">
          <p className="text-3xl font-black text-amber-700" style={{ fontFamily: 'Archivo Black' }}>{myNews.filter(n => n.status === 'pending').length}</p>
          <p className="text-[9px] font-black tracking-[0.2em] uppercase text-amber-600/60">PENDIENTES</p>
        </div>
      </div>

      {myNews.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-[#0B1F40]/15">
          <p className="text-[#0B1F40]/40 mb-4">Aún no has publicado noticias</p>
          <Link to="/create-news"><button className="bg-[#0B1F40] text-white text-xs font-black px-6 py-2 brutal-btn-press" style={{ boxShadow: '3px 3px 0px #E50B15' }}>CREAR PRIMERA NOTICIA</button></Link>
        </div>
      ) : (
        <div className="border-2 border-[#0B1F40] brutal-shadow">
          <div className="bg-[#0B1F40] text-white px-4 py-2">
            <span className="text-[10px] font-black tracking-[0.2em] uppercase">MIS PUBLICACIONES</span>
          </div>
          <div className="divide-y divide-[#0B1F40]/8">
            {myNews.map(item => {
              const st = SM[item.status] || SM.pending;
              return (
                <div key={item.id} data-testid={`my-news-${item.id}`} className={`p-4 flex items-start justify-between gap-4 border-l-4 hover:bg-[#F4F5F7] transition-colors ${st.c}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">{st.l}</span>
                      <span className="text-[9px] font-black text-[#E50B15]">{item.category}</span>
                    </div>
                    <h3 className="font-black text-sm text-[#0B1F40] truncate" style={{ fontFamily: 'Archivo Black' }}>{item.title}</h3>
                    <p className="text-[10px] text-[#0B1F40]/30 mt-1 flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{fmt(item.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {item.status === 'approved' && <Link to={`/news/${item.id}`}><button data-testid={`view-news-${item.id}`} className="border-2 border-[#0B1F40]/15 h-7 w-7 flex items-center justify-center hover:border-[#0B1F40] transition-colors"><Eye className="h-3 w-3" /></button></Link>}
                    <Link to={`/edit-news/${item.id}`}><button data-testid={`edit-news-${item.id}`} className="border-2 border-[#0B1F40]/15 h-7 w-7 flex items-center justify-center hover:border-[#0B1F40] transition-colors"><Edit className="h-3 w-3" /></button></Link>
                    <button data-testid={`delete-news-${item.id}`} onClick={() => del(item.id)} className="border-2 border-[#E50B15]/20 h-7 w-7 flex items-center justify-center text-[#E50B15] hover:border-[#E50B15] hover:bg-red-50 transition-colors"><Trash2 className="h-3 w-3" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
